import { IS_JEST_TEST } from '@scans/components/Scans/hooks';
import type { TrenchesProps } from '@trenches/components/types';
import { pointSelectors, trenchSelectors } from '@trenches/redux';
import type { Point, Trench } from '@trenches/types';
import { viewer3dSelectors } from '@viewer3D/redux';
import type { BoundingBoxWithHeight } from '@viewer3D/types';
import { useSelector } from 'react-redux';
import { buildPointTrenchMap } from './Trench/Points/helper';

export const getReducedViewport = (viewport: BoundingBoxWithHeight) => {
  if (IS_JEST_TEST) {
    return viewport;
  }

  // Smaller factor = smaller viewport = less scans and trenches rendered
  const smallerFactorHeight = 1;
  const smallerFactorWidth = 1;
  const centerXShift = 0;

  const baseCenterX = (viewport.minX + viewport.maxX) / 2;
  const baseCenterY = (viewport.minY + viewport.maxY) / 2;
  const width = viewport.maxX - viewport.minX;
  const shiftedCenterX = baseCenterX + width * centerXShift;
  const halfWidth = ((viewport.maxX - viewport.minX) / 2) * smallerFactorWidth;
  const halfHeight = ((viewport.maxY - viewport.minY) / 2) * smallerFactorHeight;

  const intersectedViewport = {
    minX: shiftedCenterX - halfWidth,
    maxX: shiftedCenterX + halfWidth,
    minY: baseCenterY - halfHeight,
    maxY: baseCenterY + halfHeight,
  };

  return intersectedViewport;
};

interface GetTrenchesInScopeProps {
  allTrenches: Trench[];
  pointEntities: Record<string, Point | undefined>;
  selectedProjectId?: string;
  selectedTrenchId?: string;
  viewport?: BoundingBoxWithHeight;
}

export const getTrenchesInScope = ({
  allTrenches,
  pointEntities,
  selectedProjectId,
  selectedTrenchId,
  viewport,
}: GetTrenchesInScopeProps): Trench[] => {
  if (!selectedProjectId || !viewport) return [];

  const { minX, maxX, minY, maxY } = getReducedViewport(viewport);

  return allTrenches.filter(({ id, projectId, pointIds }) => {
    if (projectId !== selectedProjectId) return false;
    if (id === selectedTrenchId) return true; // Always show selected trench so elements in it can be selected

    let trenchMinX = Infinity;
    let trenchMaxX = -Infinity;
    let trenchMinY = Infinity;
    let trenchMaxY = -Infinity;
    let hasPoints = false;

    // Using for loop for performance reasons
    for (let i = 0; i < pointIds.length; i += 1) {
      const { x, y } = pointEntities[pointIds[i]] ?? {};
      if (x !== undefined && y !== undefined) {
        hasPoints = true;
        if (x < trenchMinX) trenchMinX = x;
        if (x > trenchMaxX) trenchMaxX = x;
        if (y < trenchMinY) trenchMinY = y;
        if (y > trenchMaxY) trenchMaxY = y;
      }
    }

    if (!hasPoints) return false;

    return trenchMaxX >= minX && trenchMinX <= maxX && trenchMaxY >= minY && trenchMinY <= maxY;
  });
};

const useTrenchesInScope = () => {
  const selectedProjectId = useSelector(viewer3dSelectors.selectedProjectId);
  const viewport = useSelector(viewer3dSelectors.cameraViewport);
  const pointEntities = useSelector(pointSelectors.selectEntities);
  const selectedTrenchId = useSelector(viewer3dSelectors.selectedTrenchId);
  const allTrenches = useSelector(trenchSelectors.selectAll);

  return getTrenchesInScope({
    allTrenches,
    pointEntities,
    selectedProjectId,
    selectedTrenchId,
    viewport,
  });
};

export const useConnect = (): TrenchesProps => {
  const trenchesInScope = useTrenchesInScope();
  const trenchIds = trenchesInScope.map(({ id }) => id);
  const pointTrenchMap = buildPointTrenchMap(trenchesInScope); // TODO: Have a better approach to increase perfomance

  return { trenchIds, pointTrenchMap };
};
